import { useEffect } from 'react';
import { Alert, App as AntApp, Button, Card, Col, DatePicker, Form, Input, InputNumber, Row, Select, Space, Switch, Typography } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { useCreatePayment, useBankAccounts, type PaymentMethod } from '../api/payments';
import { useContract, useContractsList } from '@/features/contracts/api/contracts';
import { asApiError } from '@/shared/lib/api';
import { formatUzs } from '@/shared/lib/format';

const METHODS: PaymentMethod[] = ['cash', 'bank_transfer', 'card'];

export function PaymentCreatePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const presetContract = params.get('contractId') ?? undefined;
  const { message } = AntApp.useApp();
  const [form] = Form.useForm();
  const create = useCreatePayment();
  const { data: bankAccounts } = useBankAccounts();
  const { data: contractsList } = useContractsList({ status: 'active' });
  const contractId = Form.useWatch('contractId', form);
  const { data: contract } = useContract(contractId ?? presetContract);

  useEffect(() => {
    if (presetContract) form.setFieldValue('contractId', presetContract);
  }, [presetContract, form]);

  const method = Form.useWatch('method', form) ?? 'cash';
  const historical = Form.useWatch('historical', form) ?? false;

  async function onSubmit() {
    try {
      const v = await form.validateFields();
      const payment = await create.mutateAsync({
        contractId: v.contractId,
        amount: String(BigInt(Math.round(Number(v.amount) * 100))),
        paymentDate: v.paymentDate.format('YYYY-MM-DD'),
        method: v.method,
        bankAccountId: v.bankAccountId || undefined,
        reference: v.reference || undefined,
        notes: v.notes || undefined,
        historical: v.historical || undefined,
        overpaymentMode: v.overpaymentMode || undefined,
      });
      void message.success(t('payments.created'));
      navigate(`/payments/${payment.id}`);
    } catch (err) {
      if ((err as { errorFields?: unknown[] }).errorFields) return;
      void message.error(asApiError(err).message);
    }
  }

  return (
    <Space direction="vertical" size={20} style={{ width: '100%' }}>
      <div>
        <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate('/payments')}>
          {t('payments.title')}
        </Button>
        <Typography.Title level={2} style={{ margin: '8px 0 0' }}>{t('payments.newTitle')}</Typography.Title>
      </div>

      <Card>
        <Form
          form={form}
          layout="vertical"
          requiredMark={false}
          initialValues={{
            paymentDate: dayjs(),
            method: 'cash' as PaymentMethod,
            historical: false,
            overpaymentMode: 'advance',
          }}
          onFinish={onSubmit}
        >
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item name="contractId" label={t('payments.contract')} rules={[{ required: true }]}>
                <Select
                  showSearch
                  optionFilterProp="label"
                  placeholder="—"
                  options={(contractsList?.data ?? []).map((c) => ({
                    value: c.id,
                    label: `${c.contractNumber} · ${c.customer?.lastName ?? ''} ${c.customer?.firstName ?? ''}`,
                  }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label={t('payments.contractBalance')}>
                <Input
                  value={contract ? formatUzs(contract.totalAmount) : '—'}
                  readOnly
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item name="amount" label={t('payments.amount')} rules={[{ required: true }]}>
                <InputNumber<number>
                  style={{ width: '100%' }}
                  min={1}
                  step={100000}
                  formatter={(v) => `${v ?? ''}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
                  parser={(v) => Number((v ?? '').replace(/\s/g, '')) as number}
                  addonAfter="UZS"
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="paymentDate" label={t('payments.paymentDate')} rules={[{ required: true }]}>
                <DatePicker style={{ width: '100%' }} format="DD.MM.YYYY" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="method" label={t('payments.method')} rules={[{ required: true }]}>
                <Select options={METHODS.map((m) => ({ value: m, label: t(`apartments.paymentMethods.${m}`) }))} />
              </Form.Item>
            </Col>
          </Row>

          {method === 'bank_transfer' && (
            <Form.Item name="bankAccountId" label={t('payments.bankAccount')}>
              <Select
                allowClear
                placeholder="—"
                options={(bankAccounts ?? []).map((b) => ({
                  value: b.id,
                  label: `${b.code} · ${b.bankName} · ${b.accountNumber}`,
                }))}
              />
            </Form.Item>
          )}

          <Form.Item name="reference" label={t('payments.reference')}>
            <Input maxLength={200} />
          </Form.Item>
          <Form.Item name="notes" label={t('payments.notes')}>
            <Input.TextArea rows={2} maxLength={2000} />
          </Form.Item>

          <Form.Item
            name="historical"
            label={t('payments.historical')}
            valuePropName="checked"
            extra={t('payments.historicalHint')}
          >
            <Switch />
          </Form.Item>
          {historical && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message={t('payments.historicalWarning')}
            />
          )}

          <Form.Item
            name="overpaymentMode"
            label={t('payments.overpaymentMode')}
            extra={t('payments.overpaymentModeHint')}
          >
            <Select
              options={[
                { value: 'advance', label: t('payments.overpaymentModes.advance') },
                { value: 'reduce_principal', label: t('payments.overpaymentModes.reduce_principal') },
              ]}
            />
          </Form.Item>

          <Button type="primary" htmlType="submit" loading={create.isPending}>
            {t('payments.create')}
          </Button>
        </Form>
      </Card>
    </Space>
  );
}
